// src/components/ModalHistoricoRotas.js
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { MapContainer, TileLayer, Polyline, Marker, Popup, CircleMarker, useMap } from 'react-leaflet';
import api from '../services/api';
import L from 'leaflet';
import {
    FaRoute, FaTimes, FaGasPump, FaCalendarAlt, FaTachometerAlt,
    FaFlagCheckered, FaSpinner, FaMapMarkerAlt, FaPlay, FaPause,
    FaDna, FaBrain, FaEye, FaStopwatch, FaSyncAlt, FaChevronLeft,
    FaChevronRight, FaCircle, FaLock, FaUnlock, FaTrophy, FaFire,
} from 'react-icons/fa';
import { MdTimeline, MdOutlineRadar } from 'react-icons/md';

// ─── Ícones do mapa ──────────────────────────────────────────────────────────
const startIcon = L.divIcon({
    className: '', html: `<div style="background:#10b981;width:22px;height:22px;border-radius:50%;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,.4)"></div>`,
    iconSize: [22, 22], iconAnchor: [11, 11],
});
const endIcon = L.divIcon({
    className: '', html: `<div style="background:#ef4444;width:22px;height:22px;border-radius:50%;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,.4)"></div>`,
    iconSize: [22, 22], iconAnchor: [11, 11],
});
const forenseIcon = L.divIcon({
    className: '', html: `<div style="background:#f59e0b;width:18px;height:18px;border-radius:50%;border:3px solid white;box-shadow:0 0 0 3px rgba(245,158,11,.4)"></div>`,
    iconSize: [18, 18], iconAnchor: [9, 9],
});

// ─── Helpers ─────────────────────────────────────────────────────────────────
const fmtSeg = (s) => {
    if (!s || s < 60) return `${s || 0}s`;
    if (s < 3600) return `${Math.floor(s / 60)}min`;
    return `${Math.floor(s / 3600)}h${Math.floor((s % 3600) / 60).toString().padStart(2, '0')}min`;
};
const fmtHora = (d) => new Date(d).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

const CORES_PARADA = { curta: '#f59e0b', media: '#ef4444', longa: '#7c3aed' };
const DIAS = ['Hoje', 'Ontem', '-2d', '-3d', '-4d'];

// ─── Componente: move o mapa para um ponto (Forense) ────────────────────────
function MapMover({ position }) {
    const map = useMap();
    useEffect(() => {
        if (position) map.setView(position, map.getZoom(), { animate: true, duration: 0.5 });
    }, [position, map]);
    return null;
}

// ─── Componente: Radar DNA (SVG puro, sem lib extra) ─────────────────────────
function RadarDNA({ dna }) {
    const eixos = [
        { key: 'eficiencia',    label: 'Eficiência',     icon: '⚡' },
        { key: 'produtividade', label: 'Produtividade',  icon: '🏃' },
        { key: 'consistencia',  label: 'Consistência',   icon: '📐' },
        { key: 'dwell_score',   label: 'Permanência',    icon: '📍' },
        { key: 'cobertura',     label: 'Cobertura',      icon: '🗺️' },
    ];
    const N   = eixos.length;
    const CX  = 160;
    const CY  = 160;
    const R   = 110;

    const angleOf = (i) => (Math.PI * 2 * i) / N - Math.PI / 2;

    // Polígono de pontos do perfil
    const pontos = eixos.map((e, i) => {
        const v = (dna[e.key] || 0) / 100;
        const a = angleOf(i);
        return [CX + v * R * Math.cos(a), CY + v * R * Math.sin(a)];
    });
    const polyStr = pontos.map(p => p.join(',')).join(' ');

    // Grades
    const grades = [0.25, 0.5, 0.75, 1].map(f =>
        eixos.map((_, i) => {
            const a = angleOf(i);
            return [CX + f * R * Math.cos(a), CY + f * R * Math.sin(a)];
        })
    );

    return (
        <svg viewBox="0 0 320 320" style={{ width: '100%', maxWidth: 320 }}>
            {/* Grades */}
            {grades.map((pts, gi) => (
                <polygon key={gi} points={pts.map(p => p.join(',')).join(' ')}
                    fill="none" stroke="rgba(99,102,241,.15)" strokeWidth="1" />
            ))}
            {/* Eixos */}
            {eixos.map((_, i) => {
                const a = angleOf(i);
                return <line key={i} x1={CX} y1={CY} x2={CX + R * Math.cos(a)} y2={CY + R * Math.sin(a)}
                    stroke="rgba(99,102,241,.2)" strokeWidth="1" />;
            })}
            {/* Área do perfil */}
            <polygon points={polyStr} fill="rgba(99,102,241,.25)" stroke="#6366f1" strokeWidth="2" strokeLinejoin="round" />
            {/* Pontos */}
            {pontos.map((p, i) => <circle key={i} cx={p[0]} cy={p[1]} r="4" fill="#6366f1" />)}
            {/* Labels */}
            {eixos.map((e, i) => {
                const a = angleOf(i);
                const lx = CX + (R + 22) * Math.cos(a);
                const ly = CY + (R + 22) * Math.sin(a);
                return (
                    <g key={i}>
                        <text x={lx} y={ly - 6} textAnchor="middle" fontSize="18">{e.icon}</text>
                        <text x={lx} y={ly + 10} textAnchor="middle" fontSize="9" fill="#94a3b8" fontFamily="system-ui">{e.label}</text>
                        <text x={lx} y={ly + 22} textAnchor="middle" fontSize="11" fill="#6366f1" fontWeight="700" fontFamily="system-ui">{dna[e.key] || 0}%</text>
                    </g>
                );
            })}
            {/* Score central */}
            <circle cx={CX} cy={CY} r="28" fill="#1e1b4b" />
            <text x={CX} y={CY - 6} textAnchor="middle" fontSize="18" fontWeight="800" fill="#a5b4fc" fontFamily="system-ui">{dna.score_geral || 0}</text>
            <text x={CX} y={CY + 10} textAnchor="middle" fontSize="8" fill="#6366f1" fontFamily="system-ui">SCORE</text>
        </svg>
    );
}

// ─── COMPONENTE PRINCIPAL ────────────────────────────────────────────────────
export default function ModalHistoricoRotas({ motorista, onClose }) {
    const [diaIdx,    setDiaIdx]    = useState(0);   // 0=hoje, 1=ontem, ...
    const [activeTab, setActiveTab] = useState('mapa');
    const [dados,     setDados]     = useState(null);
    const [loading,   setLoading]   = useState(true);
    const [ultimaAt,  setUltimaAt]  = useState(null);
    const [polling,   setPolling]   = useState(false);

    // Forense
    const [forenseIdx,  setForenseIdx]  = useState(0);
    const [isPlaying,   setIsPlaying]   = useState(false);
    const playRef = useRef(null);

    const MEDIA_KML = 10;

    // ── Fetch ──────────────────────────────────────────────────────────────
    const fetchDados = useCallback(async (silent = false) => {
        if (!motorista?.id) return;
        if (!silent) setLoading(true);
        else setPolling(true);
        try {
            const res = await api.get(`/api/operations/historico-rotas/${motorista.id}`, {
                params: { dias: diaIdx },
            });
            setDados(res.data);
            setUltimaAt(new Date());
            setForenseIdx(0);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
            setPolling(false);
        }
    }, [motorista?.id, diaIdx]);

    useEffect(() => { fetchDados(); }, [fetchDados]);

    // Polling automático apenas para hoje
    useEffect(() => {
        if (diaIdx !== 0) return;
        const t = setInterval(() => fetchDados(true), 30000);
        return () => clearInterval(t);
    }, [diaIdx, fetchDados]);

    // ── Playback Forense ───────────────────────────────────────────────────
    const trajeto = useMemo(() => (dados?.trajeto || []).map(p => [parseFloat(p.latitude), parseFloat(p.longitude)]), [dados]);
    const trajetoCompleto = dados?.trajeto || [];

    useEffect(() => {
        if (!isPlaying) { clearInterval(playRef.current); return; }
        playRef.current = setInterval(() => {
            setForenseIdx(prev => {
                if (prev >= trajetoCompleto.length - 1) { setIsPlaying(false); return prev; }
                return prev + 1;
            });
        }, 80);
        return () => clearInterval(playRef.current);
    }, [isPlaying, trajetoCompleto.length]);

    // ── Dados derivados ────────────────────────────────────────────────────
    const kmDiario     = parseFloat(dados?.km_rodado || 0);
    const kmMensal     = parseFloat(dados?.km_mensal || 0);
    const paradas      = dados?.paradas || [];
    const dna          = dados?.dna || {};
    const consumo      = (kmDiario / MEDIA_KML).toFixed(1);

    const paradasLongas = paradas.filter(p => p.tipo === 'longa').length;
    const tempoParado   = paradas.reduce((s, p) => s + (p.duracao_segundos || 0), 0);

    const pontoForense = trajetoCompleto[forenseIdx];
    const velForense   = pontoForense?.velocidade_kmh || 0;

    // ── RENDER ─────────────────────────────────────────────────────────────
    return (
        <div style={{
            position: 'fixed', inset: 0,
            background: 'rgba(2, 6, 23, 0.85)',
            backdropFilter: 'blur(8px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 9999, padding: '16px',
        }}>
            <div style={{
                width: '100%', maxWidth: '1100px', height: '90vh',
                background: '#0f172a',
                borderRadius: '20px',
                display: 'flex', flexDirection: 'column',
                overflow: 'hidden',
                boxShadow: '0 32px 80px rgba(0,0,0,.6), 0 0 0 1px rgba(99,102,241,.2)',
                fontFamily: "'DM Sans', system-ui, sans-serif",
            }}>

                {/* ── HEADER ── */}
                <div style={{
                    padding: '20px 28px 16px',
                    background: 'linear-gradient(135deg, #1e1b4b 0%, #0f172a 100%)',
                    borderBottom: '1px solid rgba(99,102,241,.2)',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                        <div style={{ background: 'rgba(99,102,241,.2)', border: '1px solid rgba(99,102,241,.3)', padding: 10, borderRadius: 12, display: 'flex' }}>
                            <FaRoute size={22} color="#a5b4fc" />
                        </div>
                        <div>
                            <h2 style={{ margin: 0, fontSize: '1.15rem', color: '#e2e8f0', fontWeight: 700 }}>
                                Telemetria & Inteligência de Rotas
                            </h2>
                            <p style={{ margin: '2px 0 0', fontSize: '0.8rem', color: '#64748b' }}>
                                {motorista?.nome}
                                {dna.score_geral > 0 && (
                                    <span style={{ marginLeft: 10, background: 'rgba(99,102,241,.2)', color: '#a5b4fc', padding: '1px 8px', borderRadius: 10, fontSize: '0.75rem', fontWeight: 700 }}>
                                        DNA Score {dna.score_geral}
                                    </span>
                                )}
                            </p>
                        </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        {/* Polling indicator */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.72rem', color: '#475569' }}>
                            <FaSyncAlt size={10} style={{ animation: polling ? 'spin 1s linear infinite' : 'none', color: polling ? '#6366f1' : '#475569' }} />
                            {ultimaAt && `${ultimaAt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`}
                            {diaIdx === 0 && <span style={{ color: '#10b981' }}>● ao vivo</span>}
                        </div>
                        <button onClick={() => fetchDados(true)} style={{ background: 'rgba(99,102,241,.15)', border: '1px solid rgba(99,102,241,.3)', borderRadius: 8, padding: '6px 10px', color: '#a5b4fc', cursor: 'pointer', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: 5 }}>
                            <FaSyncAlt size={11} /> Atualizar
                        </button>
                        <button onClick={onClose} style={{ background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.1)', borderRadius: 8, padding: '6px 10px', color: '#64748b', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                            <FaTimes size={16} />
                        </button>
                    </div>
                </div>

                {/* ── SELETOR DE DIA ── */}
                <div style={{ display: 'flex', gap: 8, padding: '12px 28px', background: '#0f172a', borderBottom: '1px solid rgba(255,255,255,.06)', overflowX: 'auto' }}>
                    {DIAS.map((d, i) => (
                        <button key={i} onClick={() => { setDiaIdx(i); setIsPlaying(false); }}
                            style={{
                                padding: '6px 16px', borderRadius: 20, border: 'none',
                                background: diaIdx === i ? '#6366f1' : 'rgba(255,255,255,.06)',
                                color: diaIdx === i ? '#fff' : '#64748b',
                                fontWeight: diaIdx === i ? 700 : 500,
                                cursor: 'pointer', fontSize: '0.82rem', whiteSpace: 'nowrap',
                                transition: 'all .2s',
                            }}>
                            {d}
                        </button>
                    ))}
                </div>

                {/* ── KPIs ── */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 0, borderBottom: '1px solid rgba(255,255,255,.06)' }}>
                    {[
                        { icon: <FaTachometerAlt />, label: 'Km Hoje', value: `${kmDiario.toFixed(2)} km`, color: '#10b981' },
                        { icon: <FaGasPump />,       label: 'Consumo Est.', value: `${consumo} L`, color: '#f59e0b' },
                        { icon: <FaCalendarAlt />,   label: 'Acum. Mês', value: `${kmMensal.toFixed(1)} km`, color: '#3b82f6' },
                        { icon: <FaStopwatch />,     label: 'Paradas', value: `${paradas.length} (${fmtSeg(tempoParado)})`, color: '#8b5cf6' },
                        { icon: <FaTrophy />,        label: 'DNA Score', value: `${dna.score_geral || '—'}/100`, color: '#f43f5e' },
                    ].map((k, i) => (
                        <div key={i} style={{
                            padding: '14px 20px',
                            borderRight: i < 4 ? '1px solid rgba(255,255,255,.06)' : 'none',
                            display: 'flex', alignItems: 'center', gap: 12,
                        }}>
                            <div style={{ color: k.color, fontSize: '1.2rem', opacity: .9 }}>{k.icon}</div>
                            <div>
                                <div style={{ fontSize: '0.7rem', color: '#475569', textTransform: 'uppercase', letterSpacing: 1 }}>{k.label}</div>
                                <div style={{ fontSize: '0.95rem', color: '#e2e8f0', fontWeight: 700, marginTop: 2 }}>{k.value}</div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* ── TABS ── */}
                <div style={{ display: 'flex', gap: 2, padding: '0 28px', background: '#0f172a', borderBottom: '1px solid rgba(255,255,255,.06)' }}>
                    {[
                        { id: 'mapa',    icon: <FaMapMarkerAlt />,   label: 'Mapa + Paradas' },
                        { id: 'forense', icon: <MdTimeline size={16} />, label: 'Caixa Preta' },
                        { id: 'dna',     icon: <FaDna />,             label: 'DNA de Conduta' },
                    ].map(t => (
                        <button key={t.id} onClick={() => setActiveTab(t.id)}
                            style={{
                                padding: '12px 18px', background: 'none', border: 'none',
                                borderBottom: `3px solid ${activeTab === t.id ? '#6366f1' : 'transparent'}`,
                                color: activeTab === t.id ? '#a5b4fc' : '#475569',
                                fontWeight: activeTab === t.id ? 700 : 500,
                                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7,
                                fontSize: '0.85rem', transition: 'all .2s',
                            }}>
                            {t.icon} {t.label}
                        </button>
                    ))}
                </div>

                {/* ── CORPO ── */}
                <div style={{ flex: 1, overflow: 'hidden', display: 'flex' }}>
                    {loading ? (
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#475569', gap: 12 }}>
                            <FaSpinner size={32} color="#6366f1" style={{ animation: 'spin 1s linear infinite' }} />
                            <span style={{ fontSize: '0.9rem' }}>Carregando telemetria...</span>
                        </div>
                    ) : (

                    /* ════════════════════ MAPA ════════════════════ */
                    activeTab === 'mapa' ? (
                        <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
                            {/* Mapa */}
                            <div style={{ flex: 1, position: 'relative' }}>
                                {trajeto.length > 1 ? (
                                    <MapContainer bounds={trajeto} style={{ height: '100%', width: '100%' }}>
                                        <TileLayer url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" attribution="&copy; CARTO" />
                                        <Polyline positions={trajeto} color="#6366f1" weight={4} opacity={0.9} />
                                        <Marker position={trajeto[0]} icon={startIcon}>
                                            <Popup><strong>🟢 Início</strong><br />{fmtHora(trajetoCompleto[0]?.registrado_em)}</Popup>
                                        </Marker>
                                        <Marker position={trajeto[trajeto.length - 1]} icon={endIcon}>
                                            <Popup><strong>🔴 Última posição</strong><br />{fmtHora(trajetoCompleto[trajetoCompleto.length - 1]?.registrado_em)}</Popup>
                                        </Marker>
                                        {/* Marcadores de parada */}
                                        {paradas.map((p, i) => (
                                            <CircleMarker key={i}
                                                center={[parseFloat(p.latitude), parseFloat(p.longitude)]}
                                                radius={p.tipo === 'longa' ? 10 : p.tipo === 'media' ? 7 : 5}
                                                pathOptions={{ color: CORES_PARADA[p.tipo], fillColor: CORES_PARADA[p.tipo], fillOpacity: 0.85, weight: 2 }}
                                            >
                                                <Popup>
                                                    <strong>{p.tipo === 'longa' ? '🟣 Parada Longa' : p.tipo === 'media' ? '🔴 Parada Média' : '🟡 Parada Curta'}</strong><br />
                                                    ⏱ {fmtSeg(p.duracao_segundos)}<br />
                                                    🕐 {fmtHora(p.inicio)} → {fmtHora(p.fim)}
                                                </Popup>
                                            </CircleMarker>
                                        ))}
                                    </MapContainer>
                                ) : (
                                    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#0f172a', color: '#475569' }}>
                                        <FaMapMarkerAlt size={40} style={{ marginBottom: 12, opacity: .3 }} />
                                        <span>Sem dados de GPS para este dia</span>
                                    </div>
                                )}
                                {/* Legenda paradas */}
                                <div style={{ position: 'absolute', bottom: 16, right: 16, background: 'rgba(15,23,42,.9)', border: '1px solid rgba(255,255,255,.1)', borderRadius: 10, padding: '10px 14px', zIndex: 1000, fontSize: '0.75rem', color: '#94a3b8' }}>
                                    <div style={{ marginBottom: 4, fontWeight: 700, color: '#e2e8f0' }}>Paradas</div>
                                    {[['longa','>20min','#7c3aed'],['media','5-20min','#ef4444'],['curta','<5min','#f59e0b']].map(([t,l,c]) => (
                                        <div key={t} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                                            <div style={{ width: 10, height: 10, borderRadius: '50%', background: c }} />
                                            <span>{l}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Sidebar paradas */}
                            <div style={{ width: 240, background: '#0a0f1e', borderLeft: '1px solid rgba(255,255,255,.06)', overflowY: 'auto', padding: '16px 0' }}>
                                <div style={{ padding: '0 16px 12px', fontSize: '0.75rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 1 }}>
                                    Linha do Tempo ({paradas.length})
                                </div>
                                {paradas.length === 0 ? (
                                    <div style={{ padding: '20px 16px', color: '#334155', fontSize: '0.8rem', textAlign: 'center' }}>
                                        Sem paradas detectadas
                                    </div>
                                ) : paradas.map((p, i) => (
                                    <div key={i} style={{ padding: '10px 16px', borderBottom: '1px solid rgba(255,255,255,.04)', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: CORES_PARADA[p.tipo], marginTop: 4, flexShrink: 0 }} />
                                        <div>
                                            <div style={{ fontSize: '0.8rem', color: '#94a3b8', fontWeight: 600 }}>
                                                {fmtHora(p.inicio)} – {fmtHora(p.fim)}
                                            </div>
                                            <div style={{ fontSize: '0.72rem', color: '#475569', marginTop: 2 }}>
                                                ⏱ {fmtSeg(p.duracao_segundos)} • {p.tipo}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                    /* ════════════════════ FORENSE / CAIXA PRETA ════════════════════ */
                    ) : activeTab === 'forense' ? (
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                            {/* Mapa forense */}
                            <div style={{ flex: 1, position: 'relative' }}>
                                {trajeto.length > 1 ? (
                                    <MapContainer center={trajeto[0]} zoom={14} style={{ height: '100%', width: '100%' }}>
                                        <TileLayer url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" attribution="&copy; CARTO" />
                                        {/* Rota até o índice atual */}
                                        <Polyline positions={trajeto.slice(0, forenseIdx + 1)} color="#f59e0b" weight={3} opacity={0.9} />
                                        {/* Rota futura (apagada) */}
                                        {forenseIdx < trajeto.length - 1 && (
                                            <Polyline positions={trajeto.slice(forenseIdx)} color="#334155" weight={2} opacity={0.4} />
                                        )}
                                        {/* Posição atual */}
                                        {trajeto[forenseIdx] && (
                                            <Marker position={trajeto[forenseIdx]} icon={forenseIcon}>
                                                <Popup>
                                                    <strong>📍 Posição Forense</strong><br />
                                                    🕐 {pontoForense && fmtHora(pontoForense.registrado_em)}<br />
                                                    ⚡ {velForense} km/h
                                                </Popup>
                                            </Marker>
                                        )}
                                        <MapMover position={trajeto[forenseIdx]} />
                                    </MapContainer>
                                ) : (
                                    <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0f172a', color: '#475569' }}>
                                        Sem dados suficientes para o modo forense
                                    </div>
                                )}

                                {/* HUD forense */}
                                {pontoForense && (
                                    <div style={{ position: 'absolute', top: 16, left: 16, zIndex: 1000, background: 'rgba(15,23,42,.95)', border: '1px solid rgba(245,158,11,.3)', borderRadius: 12, padding: '12px 16px', minWidth: 200 }}>
                                        <div style={{ fontSize: '0.7rem', color: '#f59e0b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>⬛ Caixa Preta</div>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 14px' }}>
                                            {[
                                                ['🕐 Horário', fmtHora(pontoForense.registrado_em)],
                                                ['⚡ Velocidade', `${velForense} km/h`],
                                                ['📍 Ponto', `${forenseIdx + 1}/${trajetoCompleto.length}`],
                                                ['📊 % do dia', `${Math.round((forenseIdx / Math.max(trajetoCompleto.length - 1, 1)) * 100)}%`],
                                            ].map(([l, v]) => (
                                                <div key={l}>
                                                    <div style={{ fontSize: '0.65rem', color: '#475569' }}>{l}</div>
                                                    <div style={{ fontSize: '0.85rem', color: '#e2e8f0', fontWeight: 600 }}>{v}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Timeline scrubber */}
                            <div style={{ background: '#0a0f1e', borderTop: '1px solid rgba(255,255,255,.06)', padding: '16px 28px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                                    <button
                                        onClick={() => setIsPlaying(p => !p)}
                                        disabled={trajeto.length < 2}
                                        style={{ background: isPlaying ? '#f59e0b' : '#6366f1', border: 'none', borderRadius: '50%', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, boxShadow: `0 0 0 4px ${isPlaying ? 'rgba(245,158,11,.2)' : 'rgba(99,102,241,.2)'}` }}>
                                        {isPlaying ? <FaPause color="#fff" size={14} /> : <FaPlay color="#fff" size={14} style={{ marginLeft: 2 }} />}
                                    </button>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', color: '#475569', marginBottom: 6 }}>
                                            <span>{trajetoCompleto[0] ? fmtHora(trajetoCompleto[0].registrado_em) : '--'}</span>
                                            <span style={{ color: '#f59e0b', fontWeight: 600 }}>
                                                {trajetoCompleto[forenseIdx] ? fmtHora(trajetoCompleto[forenseIdx].registrado_em) : '--'}
                                            </span>
                                            <span>{trajetoCompleto[trajetoCompleto.length - 1] ? fmtHora(trajetoCompleto[trajetoCompleto.length - 1].registrado_em) : '--'}</span>
                                        </div>
                                        <input type="range" min={0} max={Math.max(trajetoCompleto.length - 1, 0)} value={forenseIdx}
                                            onChange={e => { setForenseIdx(+e.target.value); setIsPlaying(false); }}
                                            style={{ width: '100%', accentColor: '#f59e0b', height: 6 }}
                                        />
                                        {/* Marcadores de parada no scrubber */}
                                        <div style={{ position: 'relative', height: 6, marginTop: -6 }}>
                                            {paradas.map((p, i) => {
                                                const pct = trajetoCompleto.length > 1
                                                    ? trajetoCompleto.findIndex(pt => pt.registrado_em >= p.inicio) / (trajetoCompleto.length - 1) * 100
                                                    : 0;
                                                return (
                                                    <div key={i} title={`Parada ${p.tipo} — ${fmtSeg(p.duracao_segundos)}`}
                                                        style={{ position: 'absolute', left: `${pct}%`, top: -1, width: 8, height: 8, borderRadius: '50%', background: CORES_PARADA[p.tipo], transform: 'translateX(-50%)', pointerEvents: 'none' }} />
                                                );
                                            })}
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                                        <button onClick={() => setForenseIdx(0)} style={{ background: 'rgba(255,255,255,.06)', border: 'none', borderRadius: 6, padding: '4px 8px', color: '#64748b', cursor: 'pointer' }}>⏮</button>
                                        <button onClick={() => setForenseIdx(trajetoCompleto.length - 1)} style={{ background: 'rgba(255,255,255,.06)', border: 'none', borderRadius: 6, padding: '4px 8px', color: '#64748b', cursor: 'pointer' }}>⏭</button>
                                    </div>
                                </div>
                                <p style={{ margin: '8px 0 0', fontSize: '0.7rem', color: '#334155', textAlign: 'center' }}>
                                    Modo Forense — Reconstrução Histórica · Arraste o cursor ou pressione play para navegar no tempo
                                </p>
                            </div>
                        </div>

                    /* ════════════════════ DNA DE CONDUTA ════════════════════ */
                    ) : (
                        <div style={{ flex: 1, overflowY: 'auto', padding: '28px', display: 'flex', gap: 28, flexWrap: 'wrap', alignContent: 'flex-start' }}>

                            {/* Radar */}
                            <div style={{ flex: '0 0 340px', background: '#0a0f1e', borderRadius: 16, border: '1px solid rgba(99,102,241,.2)', padding: 24, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <div style={{ fontSize: '0.75rem', color: '#6366f1', textTransform: 'uppercase', letterSpacing: 2, fontWeight: 700, marginBottom: 16 }}>
                                    🧬 DNA de Conduta
                                </div>
                                <RadarDNA dna={dna} />
                                <div style={{ marginTop: 12, fontSize: '0.75rem', color: '#475569', textAlign: 'center', lineHeight: 1.6 }}>
                                    Baseado nos últimos 5 dias de operação<br />
                                    Score geral: <strong style={{ color: '#a5b4fc' }}>{dna.score_geral || '—'}/100</strong>
                                </div>
                            </div>

                            {/* Métricas detalhadas */}
                            <div style={{ flex: 1, minWidth: 280, display: 'flex', flexDirection: 'column', gap: 12 }}>
                                <div style={{ fontSize: '0.75rem', color: '#475569', textTransform: 'uppercase', letterSpacing: 2, fontWeight: 700 }}>Análise Detalhada</div>

                                {[
                                    { key: 'eficiencia',    label: 'Eficiência de KM',  desc: 'Km médio vs benchmark 80km/dia',   icon: '⚡', color: '#10b981' },
                                    { key: 'produtividade', label: 'Produtividade',      desc: '% do expediente em movimento',     icon: '🏃', color: '#3b82f6' },
                                    { key: 'consistencia',  label: 'Consistência',       desc: 'Regularidade de padrão diário',    icon: '📐', color: '#f59e0b' },
                                    { key: 'dwell_score',   label: 'Score de Permanência', desc: 'Qualidade de tempo nos pontos', icon: '📍', color: '#8b5cf6' },
                                    { key: 'cobertura',     label: 'Cobertura de Área',  desc: 'Diversidade de pontos visitados',  icon: '🗺️', color: '#ec4899' },
                                ].map(m => {
                                    const val = dna[m.key] || 0;
                                    return (
                                        <div key={m.key} style={{ background: '#0a0f1e', borderRadius: 12, border: '1px solid rgba(255,255,255,.06)', padding: '14px 18px' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                                <div>
                                                    <span style={{ fontSize: '1rem', marginRight: 8 }}>{m.icon}</span>
                                                    <span style={{ fontSize: '0.85rem', color: '#e2e8f0', fontWeight: 600 }}>{m.label}</span>
                                                </div>
                                                <span style={{ fontSize: '1rem', fontWeight: 800, color: m.color }}>{val}%</span>
                                            </div>
                                            <div style={{ background: 'rgba(255,255,255,.06)', borderRadius: 100, height: 6, overflow: 'hidden' }}>
                                                <div style={{ width: `${val}%`, height: '100%', background: m.color, borderRadius: 100, transition: 'width 1s ease' }} />
                                            </div>
                                            <div style={{ marginTop: 5, fontSize: '0.72rem', color: '#475569' }}>{m.desc}</div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Histórico 5 dias */}
                            <div style={{ flex: '0 0 100%', background: '#0a0f1e', borderRadius: 16, border: '1px solid rgba(255,255,255,.06)', padding: 24 }}>
                                <div style={{ fontSize: '0.75rem', color: '#475569', textTransform: 'uppercase', letterSpacing: 2, fontWeight: 700, marginBottom: 16 }}>
                                    📅 Histórico 5 Dias
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px,1fr))', gap: 12 }}>
                                    {(dados?.sessoes_historico || []).map((s, i) => (
                                        <div key={i} style={{ background: 'rgba(255,255,255,.03)', borderRadius: 10, padding: 14, border: '1px solid rgba(255,255,255,.06)' }}>
                                            <div style={{ fontSize: '0.75rem', color: '#6366f1', fontWeight: 700, marginBottom: 6 }}>
                                                {new Date(s.data_sessao + 'T00:00:00').toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: '2-digit' })}
                                            </div>
                                            <div style={{ fontSize: '0.9rem', color: '#e2e8f0', fontWeight: 700 }}>{parseFloat(s.km_total).toFixed(1)} km</div>
                                            <div style={{ fontSize: '0.72rem', color: '#475569', marginTop: 3 }}>
                                                {s.paradas_count} paradas · {fmtSeg(s.tempo_total_seg)}
                                            </div>
                                            {/* Mini barra proporcional */}
                                            <div style={{ marginTop: 8, background: 'rgba(255,255,255,.06)', borderRadius: 100, height: 3 }}>
                                                <div style={{ width: `${Math.min(100, (parseFloat(s.km_total) / 120) * 100)}%`, height: '100%', background: '#6366f1', borderRadius: 100 }} />
                                            </div>
                                        </div>
                                    ))}
                                    {(!dados?.sessoes_historico?.length) && (
                                        <div style={{ gridColumn: '1/-1', color: '#334155', fontSize: '0.85rem', padding: '20px 0', textAlign: 'center' }}>
                                            Ainda sem histórico de 5 dias acumulado
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )
                    )}
                </div>

                {/* Keyframes */}
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
        </div>
    );
}